import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, map, mergeMap, Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import { GarminPilotProductDetail } from '../models/garmin-pilot/gp-product-detail.model';
import { GarminPilotProduct } from '../models/garmin-pilot/gp-product.model';
import { GarminPilotSubscriptionType } from '../models/garmin-pilot/gp-subscription-type.model';
import { Locale } from '../models/locale.model';
import { LocaleService } from './locale.service';

const API_URL = `${environment.flyLegacyUrl}/garmin-pilot/api`;
const CATALOG_URL = `${API_URL}/catalog/`;
const PRODUCT_URL = `${API_URL}/products/`;
const SUBSCRIPTION_TYPES_URL = `${API_URL}/subscription-types/`;

@Injectable()
export class GarminPilotCatalogService {
  private readonly locale$: Observable<Locale>;
  private readonly renewalLocale$: Observable<Locale>;
  private readonly $PRODUCTS: Observable<Record<string, GarminPilotProductDetail>>;

  constructor(
    private readonly http: HttpClient,
    localeService: LocaleService
  ) {
    this.locale$ = localeService.getSelectedLocale();
    this.renewalLocale$ = localeService.getAutoRenewalLocale();

    this.$PRODUCTS = this.http.get<GarminPilotProductDetail[]>(PRODUCT_URL).pipe(
      map((products) => {
        return this.indexBy(products, 'partNumber');
      })
    );
  }

  getAllProductsWithSubscriptionTypes(): Observable<GarminPilotProduct[]> {
    return forkJoin([this.getSubscriptionTypes(), this.getAllProducts()]).pipe(
      map(([subscriptionTypes, products]) => {
        const subscriptionTypesByID = this.indexBy(subscriptionTypes, 'id');

        for (const product of products) {
          if (product.subscriptionTypeID != null) {
            product.subscriptionType = subscriptionTypesByID[product.subscriptionTypeID];
          }
        }

        return products.filter((product) => product.subscriptionType != null);
      })
    );
  }

  getProductByPartNumber(partNumber: string): Observable<GarminPilotProduct[]> {
    return this.getAllProductsWithSubscriptionTypes().pipe(
      map((products) => {
        return products.filter((product) => product.partNumber === partNumber);
      })
    );
  }

  getSubscriptionTypes(): Observable<GarminPilotSubscriptionType[]> {
    const subscriptionTypes = this.http.get<GarminPilotSubscriptionType[]>(SUBSCRIPTION_TYPES_URL);
    return forkJoin([subscriptionTypes, this.getAllProducts(this.renewalLocale$)]).pipe(
      map(([subTypes, products]) => {
        const productsBySKU = this.indexBy(products, 'partNumber');
        for (const subType of subTypes) {
          const renewalProduct = productsBySKU[subType.renewalProductSKU];
          subType.renewalProduct = renewalProduct;
          if (renewalProduct != null) {
            renewalProduct.subscriptionType = subType;
          }
        }

        return subTypes;
      })
    );
  }

  private getAllProducts(locale$: Observable<Locale> = this.locale$): Observable<GarminPilotProduct[]> {
    const subscriptions = locale$.pipe(
      mergeMap((locale: Locale) =>
        this.http.get<GarminPilotProduct[]>(`${CATALOG_URL}subscriptions/${locale.wpcLocale}`)
      )
    );
    const subscriptionAddOns = locale$.pipe(
      mergeMap((locale: Locale) => this.http.get<GarminPilotProduct[]>(`${CATALOG_URL}addons/${locale.wpcLocale}`))
    );

    return forkJoin([subscriptions, subscriptionAddOns, this.$PRODUCTS]).pipe(
      map(([subs, addOns, detailProducts]) => {
        const products = subs.concat(addOns).filter((product) => detailProducts[product.partNumber] != null);

        for (const product of products) {
          Object.assign(product, detailProducts[product.partNumber]);
        }

        return products;
      })
    );
  }

  private indexBy(object: any[], key: string): Record<string, any> {
    const returnValue: Record<string, any> = {};
    object.forEach((objectEntry) => {
      returnValue[objectEntry[key]] = objectEntry;
    });
    return returnValue;
  }
}
